/**
 * Statutory Config — PAYE bands, NSSF, SHIF, Housing Levy rates.
 * Authority: HR_PAYROLL_MANAGE (write) / HR_PAYROLL_VIEW (read)
 *
 * Editing an existing config in place is supported (PUT /config/statutory/{id}) — be aware
 * that payslips already calculated under a config store their own computed amounts (they
 * aren't recalculated retroactively), but re-running "Calculate" on a period after editing
 * the config it uses will apply the new rates. For rate changes that should only apply
 * going forward, add a new dated row instead of editing an old one.
 *
 * Endpoints: POST/PUT /config/statutory, POST /config/statutory/{id}/active,
 *            GET /config/statutory, GET /config/statutory/effective?date=
 *
 * IMPORTANT: Rates are stored as percentages (e.g. 6 for 6%), not decimals (0.06).
 * The DB columns use snake_case with _percent suffix; Spring Boot maps these to
 * camelCase field names like nssfRatePercent, shifRatePercent, etc.
 *
 * PAYE bands: the backend requires each band to carry a unique `bandOrder` (int) and the
 * rate field is named `ratePercent`, not `rate` — bandOrder is assigned automatically here
 * from each row's position in the list (ascending, matching ascending lowerBound) rather
 * than exposed as a separate input, since that's exactly what it needs to be.
 */
import { useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { payrollApi } from "../api";
import type { CreateStatutoryConfigRequest, PayeBand, StatutoryConfig } from "../types";
import { useAsyncData, useMutation } from "../hooks";
import {
  Btn, Card, CardHeader, EmptyState, PageHeader,
  PayrollError, PayrollLoading, Table, TBody, TD, TH, THead, TR,
  fmt, fmtDate,
} from "../components/PayrollUI";

const emptyBand: Omit<PayeBand, "id" | "bandOrder"> = { lowerBound: 0, upperBound: null, ratePercent: 0 };

/** Default values use percentages (e.g. 6 = 6%), matching the backend DB columns. */
function emptyForm(): CreateStatutoryConfigRequest {
  return {
    effectiveFrom: new Date().toISOString().slice(0, 10),
    personalReliefMonthly: 2400,
    nssfTier1Limit: 9000,
    nssfTier2Limit: 108000,
    nssfRatePercent: 6,
    shifRatePercent: 2.75,
    shifMinimumAmount: 300,
    housingLevyEmployeeRatePercent: 1.5,
    housingLevyEmployerRatePercent: 1.5,
    notes: null,
    payeBands: [{ ...emptyBand, bandOrder: 1 }],
  };
}

/** Pre-fill the form from an existing config for editing. */
function formFromConfig(c: StatutoryConfig): CreateStatutoryConfigRequest {
  return {
    effectiveFrom: c.effectiveFrom,
    personalReliefMonthly: c.personalReliefMonthly,
    nssfTier1Limit: c.nssfTier1Limit,
    nssfTier2Limit: c.nssfTier2Limit,
    nssfRatePercent: c.nssfRatePercent,
    shifRatePercent: c.shifRatePercent,
    shifMinimumAmount: c.shifMinimumAmount,
    housingLevyEmployeeRatePercent: c.housingLevyEmployeeRatePercent,
    housingLevyEmployerRatePercent: c.housingLevyEmployerRatePercent,
    notes: c.notes ?? null,
    payeBands: c.payeBands.length
      ? c.payeBands.map((b, i) => ({
          bandOrder: i + 1,
          lowerBound: b.lowerBound,
          upperBound: b.upperBound ?? null,
          ratePercent: b.ratePercent,
        }))
      : [{ ...emptyBand, bandOrder: 1 }],
  };
}

function ConfigForm({
  initial, onCancel, onSave, saving, error,
}: {
  initial: CreateStatutoryConfigRequest;
  onCancel: () => void;
  onSave: (payload: CreateStatutoryConfigRequest) => void;
  saving: boolean;
  error?: string | null;
}) {
  const [form, setForm] = useState<CreateStatutoryConfigRequest>(initial);

  function setField<K extends keyof CreateStatutoryConfigRequest>(k: K, v: CreateStatutoryConfigRequest[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setBand(i: number, patch: Partial<PayeBand>) {
    setForm((f) => ({ ...f, payeBands: f.payeBands.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  }

  /** Keep bandOrder in sync with each row's position (1-indexed) whenever the list changes —
   *  the backend requires a unique, correctly-ordered bandOrder per band. */
  function renumber(bands: PayeBand[]): PayeBand[] {
    return bands.map((b, idx) => ({ ...b, bandOrder: idx + 1 }));
  }

  function addBand() {
    setForm((f) => ({ ...f, payeBands: renumber([...f.payeBands, { ...emptyBand, bandOrder: 0 }]) }));
  }

  function removeBand(i: number) {
    setForm((f) => ({ ...f, payeBands: renumber(f.payeBands.filter((_, idx) => idx !== i)) }));
  }

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

  return (
    <Card>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block text-xs font-semibold text-slate-500">
            Effective From
            <input type="date" className={`mt-1 ${inp}`} value={form.effectiveFrom} onChange={(e) => setField("effectiveFrom", e.target.value)} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            Personal Relief (KES/mo)
            <input type="number" className={`mt-1 ${inp}`} value={form.personalReliefMonthly} onChange={(e) => setField("personalReliefMonthly", Number(e.target.value))} />
          </label>
          <div />
          <label className="block text-xs font-semibold text-slate-500">
            NSSF Tier I Limit
            <input type="number" className={`mt-1 ${inp}`} value={form.nssfTier1Limit} onChange={(e) => setField("nssfTier1Limit", Number(e.target.value))} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            NSSF Tier II Limit
            <input type="number" className={`mt-1 ${inp}`} value={form.nssfTier2Limit} onChange={(e) => setField("nssfTier2Limit", Number(e.target.value))} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            NSSF Rate (% e.g. 6 = 6%)
            <input type="number" step="0.01" className={`mt-1 ${inp}`} value={form.nssfRatePercent} onChange={(e) => setField("nssfRatePercent", Number(e.target.value))} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            SHIF Rate (% of gross pay)
            <input type="number" step="0.01" className={`mt-1 ${inp}`} value={form.shifRatePercent} onChange={(e) => setField("shifRatePercent", Number(e.target.value))} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            SHIF Minimum (KES)
            <input type="number" className={`mt-1 ${inp}`} value={form.shifMinimumAmount} onChange={(e) => setField("shifMinimumAmount", Number(e.target.value))} />
          </label>
          <div />
          <label className="block text-xs font-semibold text-slate-500">
            Housing Levy — Employee Rate (%)
            <input type="number" step="0.01" className={`mt-1 ${inp}`} value={form.housingLevyEmployeeRatePercent} onChange={(e) => setField("housingLevyEmployeeRatePercent", Number(e.target.value))} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            Housing Levy — Employer Rate (%)
            <input type="number" step="0.01" className={`mt-1 ${inp}`} value={form.housingLevyEmployerRatePercent} onChange={(e) => setField("housingLevyEmployerRatePercent", Number(e.target.value))} />
          </label>
          <label className="block text-xs font-semibold text-slate-500">
            Notes
            <input className={`mt-1 ${inp}`} value={form.notes ?? ""} onChange={(e) => setField("notes", e.target.value || null)} placeholder="Optional" />
          </label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">PAYE Bands</p>
            <Btn size="sm" icon={Plus} onClick={addBand}>Add Band</Btn>
          </div>
          <div className="space-y-2">
            {form.payeBands.map((b, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                  Lower Bound
                  <input type="number" className="w-32 rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-indigo-400" value={b.lowerBound} onChange={(e) => setBand(i, { lowerBound: Number(e.target.value) })} />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                  Upper Bound (blank = no limit)
                  <input type="number" className="w-36 rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-indigo-400" value={b.upperBound ?? ""} onChange={(e) => setBand(i, { upperBound: e.target.value ? Number(e.target.value) : null })} />
                </label>
                <div className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                  Amount in Band
                  <div className="flex h-[34px] w-32 items-center rounded-lg border border-slate-100 bg-slate-100/70 px-2 text-sm text-slate-600">
                    {b.upperBound != null ? fmt(Math.max(0, b.upperBound - b.lowerBound)) : "No limit"}
                  </div>
                </div>
                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                  Rate (% e.g. 10 = 10%)
                  <input type="number" step="0.1" className="w-28 rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-indigo-400" value={b.ratePercent} onChange={(e) => setBand(i, { ratePercent: Number(e.target.value) })} />
                </label>
                <button type="button" onClick={() => removeBand(i)} className="ml-auto rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <Btn onClick={onCancel}>Cancel</Btn>
          <Btn variant="primary" loading={saving} loadingText="Saving…" onClick={() => onSave(form)}>Save Config</Btn>
        </div>
      </div>
    </Card>
  );
}

export default function PayrollStatutoryConfigPage() {
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<StatutoryConfig | null>(null);
  const [previewDate, setPreviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [previewResult, setPreviewResult] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data, loading, error, reload } = useAsyncData(() => payrollApi.listStatutoryConfigs(), []);
  const create = useMutation((payload: CreateStatutoryConfigRequest) => payrollApi.createStatutoryConfig(payload));
  const update = useMutation((payload: { id: number; body: CreateStatutoryConfigRequest }) =>
    payrollApi.updateStatutoryConfig(payload.id, payload.body));
  const setActive = useMutation((payload: { id: number; active: boolean }) => payrollApi.setStatutoryConfigActive(payload.id, payload.active));

  function openCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function openEdit(c: StatutoryConfig) {
    setEditing(c);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function doCreate(payload: CreateStatutoryConfigRequest) {
    await create.mutate(payload);
    closeForm();
    reload();
  }

  async function doUpdate(id: number, payload: CreateStatutoryConfigRequest) {
    await update.mutate({ id, body: payload });
    closeForm();
    reload();
  }

  async function doPreview() {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewResult(null);
    try {
      const cfg = await payrollApi.getEffectiveStatutoryConfig(previewDate);
      setPreviewResult(
        `Effective ${fmtDate(cfg.effectiveFrom)} — Personal Relief ${fmt(cfg.personalReliefMonthly)}, ` +
        `NSSF ${cfg.nssfRatePercent}%, SHIF ${cfg.shifRatePercent}%`
      );
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : "No config found for that date");
    } finally {
      setPreviewLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance · Setup"
        title="Statutory Configuration"
        actions={canManage && !showForm && <Btn variant="primary" icon={Plus} onClick={openCreate}>New Config</Btn>}
      />

      <Card noPad>
        <CardHeader title="Preview Effective Config" />
        <div className="flex flex-wrap items-end gap-3 p-5">
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
            Date
            <input type="date" className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} />
          </label>
          <Btn icon={Search} loading={previewLoading} loadingText="Checking…" onClick={doPreview}>Check</Btn>
          {previewResult && <p className="text-sm font-medium text-emerald-700">{previewResult}</p>}
          {previewError && <p className="text-sm text-red-600">{previewError}</p>}
        </div>
      </Card>

      {showForm && canManage && (
        <ConfigForm
          initial={editing ? formFromConfig(editing) : emptyForm()}
          onCancel={closeForm}
          onSave={(payload) => (editing ? doUpdate(editing.id, payload) : doCreate(payload))}
          saving={editing ? update.loading : create.loading}
          error={editing ? update.error : create.error}
        />
      )}

      {loading && <PayrollLoading label="Loading statutory configs…" />}
      {error && <PayrollError message={error} onRetry={reload} />}

      {!loading && data && (
        <Table>
          <THead>
            <TH>Effective</TH><TH right>Personal Relief</TH><TH right>NSSF Rate</TH>
            <TH right>SHIF Rate</TH><TH right>Housing Levy</TH><TH>Bands</TH><TH>Status</TH><TH right>Actions</TH>
          </THead>
          <TBody>
            {data.length === 0 ? (
              <tr><td colSpan={8}><EmptyState icon={Search} title="No statutory configs" description="Add the first config to start calculating payroll." /></td></tr>
            ) : data.map((c) => (
              <TR key={c.id}>
                <TD className="font-semibold text-slate-800">{fmtDate(c.effectiveFrom)}</TD>
                <TD right>{fmt(c.personalReliefMonthly)}</TD>
                <TD right>{c.nssfRatePercent != null ? `${c.nssfRatePercent}%` : "—"}</TD>
                <TD right>{c.shifRatePercent != null ? `${c.shifRatePercent}%` : "—"}</TD>
                <TD right>
                  {c.housingLevyEmployeeRatePercent != null ? `${c.housingLevyEmployeeRatePercent}%` : "—"}
                  {" / "}
                  {c.housingLevyEmployerRatePercent != null ? `${c.housingLevyEmployerRatePercent}%` : "—"}
                </TD>
                <TD muted>{c.payeBands?.length ?? 0} bands</TD>
                <TD>
                  {c.active
                    ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Active</span>
                    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Inactive</span>}
                </TD>
                <TD right>
                  {canManage && (
                    <div className="flex justify-end gap-2">
                      <Btn size="sm" icon={Pencil} onClick={() => openEdit(c)}>Edit</Btn>
                      <Btn size="sm" variant={c.active ? "dangerOutline" : "emerald"} loading={setActive.loading}
                        onClick={() => setActive.mutate({ id: c.id, active: !c.active }).then(reload)}>
                        {c.active ? "Deactivate" : "Activate"}
                      </Btn>
                    </div>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
      {setActive.error && <p className="text-sm text-red-600">{setActive.error}</p>}
    </div>
  );
}